/**
 * Admin Question authoring page — create/edit a single Question.
 *
 * Issue 03-question-bank/01 shipped per-type Option validation. Issue
 * 03-question-bank/02 adds: category/subcategory dropdowns, tag
 * multi-select with type-to-create, benchmark domains (comma-separated,
 * author-facing only), and image upload via the Module 7 presigned-PUT
 * flow.
 *
 * Server-side validation is the authority — this page mirrors enough rules
 * for fast feedback, not as a substitute.
 */

import { useEffect, useState, useMemo, type FormEvent, type ReactElement } from 'react';
import { OptionEditor } from '../../../components/questions/option-editor.tsx';
import { UploadButton } from '../../../components/storage/upload-button.tsx';
import { QuestionPreview } from './components/QuestionPreview.tsx';
import { AdminLayout } from '../layout.tsx';
import { Button, Card, Input, Link, toast } from '@/components/ui';
import {
  confirmImageUpload,
  createQuestion,
  createTag,
  getQuestion,
  listCategories,
  listSubcategories,
  listTags,
  requestImageUploadUrl,
  updateQuestion,
  type CategoryPayload,
  type OptionInput,
  type QuestionPayload,
  type QuestionType,
  type SubcategoryPayload,
  type TagPayload,
} from '../../../state/questions/api.ts';
import { useAutosave } from '@/hooks/useAutosave';

const ALL_TYPES: QuestionType[] = [
  'multiple',
  'truefalse',
  'multi',
  'order',
  'short',
  'filltext',
  'fillnumber',
];

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export interface AdminQuestionEditPageProps {
  questionId?: string;
}

const emptyOptionsFor = (type: QuestionType): OptionInput[] => {
  switch (type) {
    case 'truefalse':
      return [
        { text: 'True', is_correct: true, sort_order: 1 },
        { text: 'False', is_correct: false, sort_order: 2 },
      ];
    case 'fillnumber':
      return [{ text: '', sort_order: 1 }];
    case 'multiple':
    case 'multi':
      return [
        { text: '', is_correct: true, sort_order: 1 },
        { text: '', is_correct: false, sort_order: 2 },
      ];
    case 'order':
      return [
        { text: '', sort_order: 1 },
        { text: '', sort_order: 2 },
      ];
    case 'short':
    case 'filltext':
      return [{ text: '', sort_order: 1 }];
  }
};

export function AdminQuestionEditPage(
  props: AdminQuestionEditPageProps,
): ReactElement {
  const { questionId } = props;
  const isEdit = typeof questionId === 'string' && questionId.length > 0;

  const [type, setType] = useState<QuestionType>('multiple');
  const [text, setText] = useState<string>('');
  const [explanation, setExplanation] = useState<string>('');
  const [opts, setOpts] = useState<OptionInput[]>(emptyOptionsFor('multiple'));

  // Taxonomy state (Story 57+58)
  const [categories, setCategories] = useState<CategoryPayload[]>([]);
  const [subcategories, setSubcategories] = useState<SubcategoryPayload[]>([]);
  const [allTags, setAllTags] = useState<TagPayload[]>([]);
  const [categoryId, setCategoryId] = useState<string>('');
  const [subcategoryId, setSubcategoryId] = useState<string>('');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [newTagInput, setNewTagInput] = useState<string>('');

  // Benchmark Domain (Story 56) — author-facing only, comma-separated.
  const [benchmarkDomainsRaw, setBenchmarkDomainsRaw] = useState<string>('');

  // Image upload (Story 55)
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<QuestionPayload | null>(null);

  // Load taxonomy lookups on mount.
  useEffect(() => {
    void (async () => {
      const c = await listCategories();
      if (c.ok) setCategories(c.data);
      const t = await listTags();
      if (t.ok) setAllTags(t.data);
    })();
  }, []);

  // Reload subcategories whenever category changes.
  useEffect(() => {
    if (!categoryId) {
      setSubcategories([]);
      setSubcategoryId('');
      return;
    }
    void (async () => {
      const s = await listSubcategories(categoryId);
      if (s.ok) setSubcategories(s.data);
    })();
  }, [categoryId]);

  // Load the Question for edit mode.
  useEffect(() => {
    if (!isEdit) return;
    void (async () => {
      const env = await getQuestion(questionId);
      if (!env.ok) {
        setError(`${env.error.code}: ${env.error.message}`);
        return;
      }
      setType(env.data.type);
      setText(env.data.text);
      setExplanation(env.data.explanation ?? '');
      setOpts(
        env.data.options.map((o) => ({
          text: o.text,
          is_correct: o.is_correct,
          sort_order: o.sort_order,
        })),
      );
      setCategoryId(env.data.category_id ?? '');
      setSubcategoryId(env.data.subcategory_id ?? '');
      setSelectedTagIds(env.data.tags.map((t) => t.id));
      setBenchmarkDomainsRaw(env.data.benchmark_domains.join(', '));
      setImageUrl(env.data.image_url);
    })();
  }, [isEdit, questionId]);

  const onTypeChange = (next: QuestionType): void => {
    setType(next);
    setOpts(emptyOptionsFor(next));
  };

  const onToggleTag = (tagId: string): void => {
    setSelectedTagIds((cur) =>
      cur.includes(tagId) ? cur.filter((x) => x !== tagId) : [...cur, tagId],
    );
  };

  const onCreateTag = async (): Promise<void> => {
    const name = newTagInput.trim();
    if (name.length === 0) return;
    const env = await createTag(name);
    if (!env.ok) {
      setError(`${env.error.code}: ${env.error.message}`);
      return;
    }
    setAllTags((cur) =>
      cur.some((t) => t.id === env.data.id) ? cur : [...cur, env.data],
    );
    setSelectedTagIds((cur) =>
      cur.includes(env.data.id) ? cur : [...cur, env.data.id],
    );
    setNewTagInput('');
  };

  const onImageUpload = async (
    file: File,
  ): Promise<{ ok: boolean; message?: string }> => {
    if (!isEdit) {
      return {
        ok: false,
        message: 'Save the Question first before attaching an image.',
      };
    }
    // 1) Request signed upload URL.
    const prep = await requestImageUploadUrl(questionId, {
      filename: file.name,
      mime_type: file.type,
      size_bytes: file.size,
    });
    if (!prep.ok) {
      return { ok: false, message: `${prep.error.code}: ${prep.error.message}` };
    }
    // 2) PUT to R2.
    let putRes: Response;
    try {
      putRes = await fetch(prep.data.upload_url, {
        method: 'PUT',
        headers: prep.data.required_headers,
        body: file,
      });
    } catch (e) {
      return {
        ok: false,
        message: `R2 PUT failed: ${e instanceof Error ? e.message : 'unknown'}`,
      };
    }
    if (!putRes.ok) {
      return { ok: false, message: `R2 PUT status ${String(putRes.status)}` };
    }
    // 3) Confirm.
    const conf = await confirmImageUpload(questionId, prep.data.artefact_key);
    if (!conf.ok) {
      return { ok: false, message: `${conf.error.code}: ${conf.error.message}` };
    }
    setImageUrl(conf.data.image_url);
    return { ok: true, message: 'Image attached.' };
  };

  // J-X12 — Autosave integration for question authoring.
  const questionData = useMemo(() => ({
    type,
    text,
    explanation,
    options: opts,
    category_id: categoryId,
    subcategory_id: subcategoryId,
    tag_ids: selectedTagIds,
    benchmark_domains: benchmarkDomainsRaw
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0),
  }), [type, text, explanation, opts, categoryId, subcategoryId, selectedTagIds, benchmarkDomainsRaw]);

  const handleAutosave = async (data: typeof questionData): Promise<void> => {
    if (!isEdit) return; // Don't autosave before first manual save creates the question
    const input = {
      ...data,
      explanation: data.explanation.length > 0 ? data.explanation : null,
    };
    const env = await updateQuestion(questionId, input);
    if (!env.ok) return; // Silent — autosave errors shown via saveStatus
    setSaved(env.data);
  };

  const { saveStatus, savedAt } = useAutosave({
    data: questionData,
    saveFn: handleAutosave,
    storageKey: isEdit ? `bimassess-autosave-question-${questionId}` : 'bimassess-autosave-question-new',
    enabled: isEdit,
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    void (async () => {
      try {
        const benchmark_domains = benchmarkDomainsRaw
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0);
        const input = {
          type,
          text,
          explanation: explanation.length > 0 ? explanation : null,
          options: opts,
          category_id: categoryId.length > 0 ? categoryId : undefined,
          subcategory_id: subcategoryId.length > 0 ? subcategoryId : undefined,
          tag_ids: selectedTagIds,
          benchmark_domains,
        };
        const env = isEdit
          ? await updateQuestion(questionId, input)
          : await createQuestion(input);
        if (!env.ok) {
          const msg = `${env.error.code}${env.error.field ? ` (${env.error.field})` : ''}: ${env.error.message}`;
          setError(msg);
          toast.error(msg);
          return;
        }
        setSaved(env.data);
        setImageUrl(env.data.image_url);
        toast.success(isEdit ? 'Question saved.' : 'Question created.');
      } finally {
        setBusy(false);
      }
    })();
  };

  const SELECT_CLASSES =
    'w-full rounded-md border border-line-2 bg-card px-3 py-2.5 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l transition-colors duration-150';
  const TEXTAREA_CLASSES =
    'w-full rounded-md border border-line-2 bg-card px-3 py-2 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l transition-colors duration-150';
  const LABEL_CLASSES =
    'mb-1 block font-mono text-eyebrow font-medium uppercase tracking-wide text-muted';

  const CARD_HEADER_CLASSES = 'border-b border-line px-6 py-4';
  const CARD_TITLE_CLASSES = 'font-sans text-title font-medium text-ink';
  const BACK_LINK_CLASSES =
    'inline-flex items-center rounded-md border border-line-2 bg-transparent px-4 py-2 font-sans text-small text-ink no-underline hover:bg-bg-warm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-l focus-visible:ring-offset-2 transition-colors duration-150';

  return (
    <AdminLayout title={isEdit ? 'Edit Question' : 'Create Question'} eyebrow='Admin · Questions'>
      {/* ─── Header CTA cluster ───────────────────────────────────────────
          Mirrors the Test Composer header: autosave indicator on the left,
          "← Back to Questions" secondary + primary Submit on the right. The
          Submit moves out of the form bottom into the page header band. */}
      <div className="mt-6 flex items-start justify-between gap-3" data-testid="question-edit-header-cta">
        <div className="flex items-center gap-2 font-mono text-mono text-muted-2" data-testid="autosave-indicator">
          {saveStatus === 'saving' && <span>Saving…</span>}
          {saveStatus === 'saved' && savedAt && (
            <span>Saved {Math.max(0, Math.floor((Date.now() - savedAt.getTime()) / 1000))}s ago</span>
          )}
          {saveStatus === 'error' && <span className="text-error">Save failed</span>}
        </div>
        <div className="flex items-start gap-3">
          <Link to="/admin/questions" className={BACK_LINK_CLASSES}>
            ← Back to Questions
          </Link>
          <Button type="submit" form="question-edit-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create question'}
          </Button>
        </div>
      </div>

      {/* Banners — surface under the header CTA, full width above the grid. */}
      {error ? (
        <p role="alert" className="mt-4 rounded-md border border-error/30 bg-error/5 px-4 py-2 font-sans text-small text-error">
          {error}
        </p>
      ) : null}
      {saved ? (
        <p role="status" className="mt-4 rounded-md border border-success/30 bg-success/5 px-4 py-2 font-sans text-small text-success">
          Saved {saved.id} ({saved.deprecated ? 'deprecated' : 'active'}).
        </p>
      ) : null}

      <form id="question-edit-form" onSubmit={onSubmit}>
        {/* ─── 40:60 editor grid ─────────────────────────────────────────────
            Desktop (lg+): Classification (2/5 ≈ 40%) | Authoring + Preview
            (3/5 ≈ 60%). Below lg: single column, stacked. Mirrors the Test
            Composer's grid idiom (lg:grid-cols-5, items-start). The right
            column is a flex stack: authoring inputs on top, live Preview
            below — "input on top, preview below". */}
        <div className="mt-5 grid grid-cols-1 items-start gap-5 lg:grid-cols-5">

          {/* ── Column 1 (2/5 = 40%): Classification ────────────────────── */}
          <section aria-labelledby="classification-heading" className="lg:col-span-2">
            <Card variant="default" className="p-0">
              <header className={CARD_HEADER_CLASSES}>
                <h2 id="classification-heading" className={CARD_TITLE_CLASSES}>Classification</h2>
              </header>
              {/* max-w-2xl caps inputs so the form stays form-shaped in the
                  40% column rather than ballooning to fill it (idiom copied
                  from the Test Composer Settings card). */}
              <div className="flex max-w-2xl flex-col gap-5 px-6 py-5">

                {/* Type */}
                <div>
                  <label htmlFor="q-type" className={LABEL_CLASSES}>
                    Question type
                  </label>
                  <select
                    id="q-type"
                    value={type}
                    onChange={(e) => onTypeChange(e.target.value as QuestionType)}
                    disabled={busy}
                    className={SELECT_CLASSES}
                  >
                    {ALL_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                {/* Category + Subcategory side-by-side on sm+ */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="q-category" className={LABEL_CLASSES}>
                      Category
                    </label>
                    <select
                      id="q-category"
                      value={categoryId}
                      onChange={(e) => setCategoryId(e.target.value)}
                      disabled={busy}
                      className={SELECT_CLASSES}
                    >
                      <option value="">(none)</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="q-subcategory" className={LABEL_CLASSES}>
                      Subcategory
                    </label>
                    <select
                      id="q-subcategory"
                      value={subcategoryId}
                      onChange={(e) => setSubcategoryId(e.target.value)}
                      disabled={busy || categoryId.length === 0}
                      className={SELECT_CLASSES}
                    >
                      <option value="">(none)</option>
                      {subcategories.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <span className={LABEL_CLASSES}>Tags</span>
                  {allTags.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {allTags.map((t) => (
                        <label
                          key={t.id}
                          className={`flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1 font-sans text-small transition-colors duration-150 ${
                            selectedTagIds.includes(t.id)
                              ? 'border-accent bg-accent/10 text-accent'
                              : 'border-line-2 bg-card text-muted hover:border-accent/50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={selectedTagIds.includes(t.id)}
                            onChange={() => onToggleTag(t.id)}
                            disabled={busy}
                          />
                          {t.name}
                        </label>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="new-tag-name"
                      value={newTagInput}
                      onChange={(e) => setNewTagInput(e.target.value)}
                      disabled={busy}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => void onCreateTag()}
                      disabled={busy || newTagInput.trim().length === 0}
                    >
                      Add tag
                    </Button>
                  </div>
                </div>

                {/* Benchmark Domains */}
                <div>
                  <label htmlFor="q-benchmark" className={LABEL_CLASSES}>
                    Benchmark domains
                  </label>
                  <Input
                    id="q-benchmark"
                    type="text"
                    value={benchmarkDomainsRaw}
                    onChange={(e) => setBenchmarkDomainsRaw(e.target.value)}
                    placeholder="UK BIM Framework, ISO 19650"
                    disabled={busy}
                  />
                  <p className="mt-1 font-sans text-small text-muted">
                    Used for author reporting only — candidates never see these values.
                  </p>
                </div>

              </div>
            </Card>
          </section>

          {/* ── Column 2 (3/5 = 60%): authoring inputs (top) + Preview (below) ─ */}
          <section aria-labelledby="authoring-heading" className="flex flex-col gap-5 lg:col-span-3">
            <h2 id="authoring-heading" className="sr-only">Question authoring and preview</h2>

            {/* TOP — the "Question" authoring card. Text, image, options, and
                explanation consolidated into one card with labelled
                sub-sections so the authoring inputs read as one continuous
                act (matching the Test Composer's single Settings card weight). */}
            <Card variant="default" className="p-0">
              <header className={CARD_HEADER_CLASSES}>
                <h2 className={CARD_TITLE_CLASSES}>Question</h2>
              </header>
              <div className="flex flex-col gap-5 px-6 py-5">

                {/* Question text */}
                <div>
                  <label htmlFor="q-text" className={LABEL_CLASSES}>
                    Question text
                  </label>
                  <textarea
                    id="q-text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={4}
                    required
                    className={TEXTAREA_CLASSES}
                  />
                </div>

                {/* Image */}
                <div>
                  <span className={LABEL_CLASSES}>Image attachment</span>
                  <p className="mb-2 font-sans text-small text-muted">
                    {imageUrl ? `Current: ${imageUrl}` : 'No image attached.'}
                  </p>
                  <UploadButton
                    accept="image/png,image/jpeg,image/webp"
                    maxBytes={MAX_IMAGE_BYTES}
                    onUpload={onImageUpload}
                    label="Attach image (PNG / JPEG / WebP, max 5 MB)"
                  />
                </div>

                {/* Answer Options */}
                <div className="border-t border-line pt-5">
                  <span className={LABEL_CLASSES}>Answer options</span>
                  <OptionEditor type={type} options={opts} onChange={setOpts} />
                </div>

                {/* Explanation */}
                <div className="border-t border-line pt-5">
                  <label htmlFor="q-explanation" className={LABEL_CLASSES}>
                    Explanation (optional)
                  </label>
                  <textarea
                    id="q-explanation"
                    value={explanation}
                    onChange={(e) => setExplanation(e.target.value)}
                    rows={3}
                    className={TEXTAREA_CLASSES}
                  />
                </div>

              </div>
            </Card>

            {/* BOTTOM — live author preview. Read-only mirror of the candidate
                render, with correct answers marked for the author. */}
            <Card variant="default" className="p-0">
              <header className={CARD_HEADER_CLASSES}>
                <h2 className={CARD_TITLE_CLASSES}>Preview</h2>
                <p className="mt-1 font-mono text-eyebrow uppercase tracking-wide text-muted">
                  How candidates see this question · correct answers marked for you
                </p>
              </header>
              <div className="px-6 py-5">
                <QuestionPreview
                  type={type}
                  text={text}
                  options={opts}
                  explanation={explanation}
                  imageUrl={imageUrl}
                />
              </div>
            </Card>
          </section>

        </div>{/* end 40:60 grid */}
      </form>
    </AdminLayout>
  );
}
